export interface EnvConfig {
  API_BASE_URL: string
  CDN_URL: string
  UPLOAD_API_BASE_URL: string
}

declare global {
  interface Window {
    __ENV__?: {
      API_BASE_URL?: string
      CDN_URL?: string
      UPLOAD_API_BASE_URL?: string
    }
  }
}

type DevApiTarget = 'local' | 'test'

const DEFAULT_LOCAL_API_BASE_URL = 'http://localhost:8081'
const DEFAULT_TEST_API_BASE_URL = 'https://test.appjoly.com'
const DEFAULT_TEST_CDN_URL = 'https://cdn.appjoly.com'

function getBaseEnvConfig(): EnvConfig {
  const windowEnv = typeof window !== 'undefined' ? window.__ENV__ || {} : {}

  return {
    API_BASE_URL: windowEnv.API_BASE_URL || import.meta.env.VITE_API_BASE_URL || '',
    CDN_URL: windowEnv.CDN_URL || import.meta.env.VITE_CDN_URL || '',
    UPLOAD_API_BASE_URL: windowEnv.UPLOAD_API_BASE_URL || import.meta.env.VITE_UPLOAD_API_BASE_URL || '',
  }
}

function getDevApiTarget(): DevApiTarget {
  return import.meta.env.VITE_DEV_API_TARGET === 'test' ? 'test' : 'local'
}

export function getEnvConfig(): EnvConfig {
  const baseConfig = getBaseEnvConfig()

  if (!import.meta.env.DEV) {
    return baseConfig
  }

  const devApiTarget = getDevApiTarget()

  if (devApiTarget === 'test') {
    return {
      API_BASE_URL: import.meta.env.VITE_DEV_TEST_API_BASE_URL || DEFAULT_TEST_API_BASE_URL,
      CDN_URL: import.meta.env.VITE_DEV_TEST_CDN_URL || baseConfig.CDN_URL || DEFAULT_TEST_CDN_URL,
      UPLOAD_API_BASE_URL: import.meta.env.VITE_DEV_TEST_UPLOAD_API_BASE_URL || baseConfig.UPLOAD_API_BASE_URL || '',
    }
  }

  return {
    API_BASE_URL: import.meta.env.VITE_DEV_LOCAL_API_BASE_URL || baseConfig.API_BASE_URL || DEFAULT_LOCAL_API_BASE_URL,
    CDN_URL: import.meta.env.VITE_DEV_LOCAL_CDN_URL || baseConfig.CDN_URL,
    UPLOAD_API_BASE_URL: import.meta.env.VITE_DEV_LOCAL_UPLOAD_API_BASE_URL || baseConfig.UPLOAD_API_BASE_URL || '',
  }
}

function isProductionHost(hostname: string): boolean {
  const host = hostname.trim().toLowerCase()
  if (!host || host === 'localhost' || host === '127.0.0.1') {
    return false
  }
  if (host.includes('test.') || host.includes('staging.') || host.includes('dev.')) {
    return false
  }
  return true
}

export function isProductionRuntime(env: EnvConfig): boolean {
  const apiBaseURL = (env.API_BASE_URL || '').trim()
  if (apiBaseURL) {
    try {
      return isProductionHost(new URL(apiBaseURL).hostname)
    } catch {
      if (apiBaseURL.includes('localhost') || apiBaseURL.includes('127.0.0.1')) {
        return false
      }
      if (apiBaseURL.includes('test.') || apiBaseURL.includes('staging.') || apiBaseURL.includes('dev.')) {
        return false
      }
      return true
    }
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    return isProductionHost(window.location.hostname)
  }
  return false
}
